import React from 'react';
import Dialog, { Content, DialogActions, Title } from 'ui/views/dialogs/Dialog';
import Button from 'ui/elements/buttons/Button';
import { KpiEmbed, KpiWithDisplay } from 'domain/kpis/types';
import ShareKpiDialog from 'domain/kpis/ShareKpi/ShareKpiDialog';
import useResource from 'util/resource/useResource';
import Resources from 'util/resource/Resources';
import kpiAPI from 'apis/KpiAPI';
import { contentSpacing } from 'ui/theme/themeConstants';
import Card from 'ui/views/cards/Card';
import { kpiAsEmbed } from 'domain/kpis/utils';
import { TinyCompanyDTO } from 'types/company';

interface Props {
  company: TinyCompanyDTO;
  onKpiSelected: (embed: KpiEmbed) => void;
  onClose: () => void;
  existingEmbed?: KpiEmbed;
}

export default function KpiEmbedSelector({ company, onKpiSelected, onClose, existingEmbed }: Props) {
  const { resource: kpisResource } = useResource<Record<string, KpiWithDisplay>>(kpiAPI.kpi.list(company.slug));

  const handleEmbedCreated = (embed: KpiEmbed) => {
    onKpiSelected(embed);
    onClose();
  };

  const handleKpiClick = (kpi: KpiWithDisplay) => {
    onKpiSelected(kpiAsEmbed(kpi, company));
    onClose();
  };

  // When editing, find the KPI and go directly to config
  if (existingEmbed) {
    return (
      <Resources resources={[kpisResource]}>
        {([kpis]) => {
          const kpi = Object.values(kpis).find(k => k.details.id === existingEmbed.kpiId);

          if (!kpi) {
            return (
              <Dialog open onClose={onClose} mobileLayout="fullscreen">
                <Title onClose={onClose}>Error</Title>
                <Content>
                  <div className="text-grey text-italic u-content-padding">
                    This KPI has been deleted or is no longer available.
                  </div>
                </Content>
                <DialogActions>
                  <Button kind="secondary" onClick={onClose}>
                    Close
                  </Button>
                </DialogActions>
              </Dialog>
            );
          }

          return (
            <ShareKpiDialog
              kpi={kpi}
              company={company}
              onClose={onClose}
              onEmbedCreated={handleEmbedCreated}
              existingEmbed={existingEmbed}
            />
          );
        }}
      </Resources>
    );
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" mobileLayout="fullscreen">
      <Title onClose={onClose}>Embed KPI</Title>
      <Content>
        <p className="text-body u-content-spacing-bottom">Select a KPI to embed in your update.</p>
        <p className="text-small text-grey u-content-spacing-bottom">
          Note: Embedded KPIs will be visible to anyone who can see this post.
        </p>
        <Resources resources={[kpisResource]}>
          {([kpis]) => {
            const kpiList = Object.values(kpis).filter(kpi => !kpi.details.archivedAt);

            if (kpiList.length === 0) {
              return (
                <div className="text-body text-italic u-content-padding">
                  No KPIs available. Create a KPI first to embed it in updates.
                </div>
              );
            }

            return (
              <div style={{ display: 'grid', gap: contentSpacing }}>
                {kpiList.map(kpi => (
                  <Card hover="highlight" key={kpi.details.id} elevation={2} onClick={() => handleKpiClick(kpi)}>
                    <div>
                      <h5 className="text-h5">{kpi.details.name}</h5>
                      {kpi.details.unitOfMeasure && (
                        <div className="text-small text-grey">Unit: {kpi.details.unitOfMeasure}</div>
                      )}
                      <div className="text-small text-grey u-quarter-spacing-top">
                        {kpi.values.length} data point{kpi.values.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            );
          }}
        </Resources>
      </Content>
      <DialogActions>
        <Button kind="secondary" onClick={onClose}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
